import { Fragment, useState } from "react";

import { HttpError } from "../api/rest";
import type { AIAskView, AIVerdict } from "../api/types";
import { useAIAsk, useAIAsks, useAskAI } from "../hooks/useAI";
import {
  actionLabel,
  agreement,
  extraAnswers,
  inconsistent,
  money,
  pct,
  providerLabel,
  statusWord,
  verdictTone,
  verdictWord,
} from "../lib/ai";
import { formatTime } from "../lib/format";
import { activeSizeOf, useSettings } from "../store/useSettings";
import { Empty, Panel } from "./Panel";

const toneClass: Record<ReturnType<typeof verdictTone>, string> = {
  good: "text-[var(--color-good)]",
  bad: "text-[var(--color-bad)]",
  warn: "text-[var(--color-warn)]",
  muted: "text-[var(--color-muted)]",
};

/**
 * ИИ-судья на странице рынка (ADR-0038).
 *
 * Кнопка задаёт ОДИН И ТОТ ЖЕ вопрос «ставить или не ставить на этом окне
 * сейчас» двум провайдерам параллельно — TypeSafe Jev и OpenAI Decisions, — и
 * ответы ложатся рядом, чтобы их можно было сравнить глазами, а позже — по
 * сводке на странице сигналов.
 *
 * # Чего на панели НЕТ и не будет
 *
 * Кнопки ордера. У судьи нет порта постановки — не «выключен флагом», а нет
 * в зависимостях вовсе, — и кнопка «купить по совету» здесь сделала бы из
 * сравнения торговлю в обход этого устройства. Бюджет — сумма, выбранная на
 * панели быстрых действий, — нужен только для теневого счёта: тейкер-план на
 * эту сумму по живому стакану.
 */
export function AIPanel({ conditionId }: { conditionId: string }) {
  const budget = useSettings((s) => activeSizeOf(s.settings));
  const ask = useAskAI(conditionId);
  // Выбранный оператором запрос — вместе с рынком, на котором его выбрали:
  // выбор с прошлого окна на новом не действует. Без выбора — последний
  // запрос этого рынка: оператор ждёт его свежий ответ, а не пустую панель.
  const [picked, setPicked] = useState<{ cond: string; id: string } | null>(null);
  const history = useAIAsks(conditionId);
  const askId = (picked?.cond === conditionId ? picked.id : null) ?? history.data?.asks[0]?.id ?? null;
  const current = useAIAsk(askId);

  const pending = ask.isPending || (current.data !== undefined && !current.data.terminal);
  const error =
    ask.error instanceof HttpError ? ask.error.message : ask.error ? "запрос не отправлен" : null;

  const onAsk = () => {
    if (!budget) return;
    ask.mutate(budget, { onSuccess: (r) => setPicked({ cond: conditionId, id: r.ask_id }) });
  };

  return (
    <Panel
      title="ИИ-судья"
      badge="dry"
      subtitle="ордеров не ставит · TypeSafe Jev ‖ OpenAI Decisions на одном снимке"
      actions={
        <button
          type="button"
          disabled={!budget || pending}
          onClick={onAsk}
          title={budget ? `теневой счёт на $${budget}` : "выберите сумму на панели быстрых действий"}
          className="rounded border border-[var(--color-border)] px-2 py-0.5 text-xs disabled:opacity-50"
        >
          {pending ? "спрашиваю…" : `Спросить ИИ${budget ? ` · $${budget}` : ""}`}
        </button>
      }
    >
      {error && <p className="mb-2 text-xs text-[var(--color-bad)]">{error}</p>}
      {askId === null ? (
        history.isPending ? (
          <Empty>загрузка…</Empty>
        ) : (
          <p className="text-xs text-[var(--color-muted)]">
            Кнопка отправит обоим провайдерам стратегию из ai/strategy.md и снимок окна: индекс и
            страйк, физику замка, оба стакана, цену покупки на выбранную сумму и накопления
            кошельков. Ответы и исход окна сохраняются для сравнения.
          </p>
        )
      ) : current.data ? (
        <AskCard v={current.data} />
      ) : (
        <Empty>{current.isError ? "запрос не загрузился" : "загрузка…"}</Empty>
      )}
      <History
        asks={history.data?.asks ?? []}
        shown={askId}
        onPick={(id) => setPicked({ cond: conditionId, id })}
      />
    </Panel>
  );
}

function AskCard({ v }: { v: AIAskView }) {
  const s = v.snapshot;
  const agree = agreement(v.verdicts);
  const byProvider = new Map(v.verdicts.map((x) => [x.provider, x]));
  const providers = s?.providers.length ? s.providers : v.verdicts.map((x) => x.provider);
  return (
    <div className="flex flex-col gap-2 text-xs">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[var(--color-muted)]">
        <span>{formatTime(v.requested_at)}</span>
        <span>{statusWord(v)}</span>
        {s?.context_rev && <span>контекст {s.context_rev}</span>}
        {s?.mid_up !== undefined && <span>рынок P(Up) {pct(s.mid_up)}</span>}
        {s?.phys_p_up !== undefined && <span>модель замка P(Up) {pct(s.phys_p_up)}</span>}
        {agree === "agree" && <span className="text-[var(--color-good)]">провайдеры согласны</span>}
        {agree === "disagree" && <span className="text-[var(--color-warn)]">провайдеры расходятся</span>}
      </div>
      {s && s.inputs_missing.length > 0 && (
        <p className="text-[var(--color-warn)]">в снимке нет: {s.inputs_missing.join(", ")}</p>
      )}
      {providers.length > 0 && (
        <div className="grid gap-2 sm:grid-cols-2">
          {providers.map((p) => (
            <VerdictCard key={p} provider={p} v={byProvider.get(p)} />
          ))}
        </div>
      )}
      {s?.input_text && (
        <details>
          <summary className="cursor-pointer text-[var(--color-muted)]">вход провайдерам</summary>
          <pre className="mt-1 max-h-80 overflow-auto whitespace-pre-wrap rounded border border-[var(--color-border)] p-2 text-[11px]">
            {s.input_text}
          </pre>
        </details>
      )}
    </div>
  );
}

function VerdictCard({ provider, v }: { provider: string; v: AIVerdict | undefined }) {
  return (
    <div className="rounded border border-[var(--color-border)] p-2">
      <div className="flex items-baseline justify-between gap-2">
        <span className="font-medium">{providerLabel(provider)}</span>
        {v && <span className="text-[var(--color-muted)]">{v.latency_ms} мс</span>}
      </div>
      {!v ? (
        <p className="mt-1 text-[var(--color-muted)]">ждём ответа…</p>
      ) : (
        <>
          <p className={`mt-1 font-medium ${toneClass[verdictTone(v)]}`}>{verdictWord(v)}</p>
          <dl className="mt-1 grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5">
            <dt className="text-[var(--color-muted)]">P(Up)</dt>
            <dd>{pct(v.p_up)}</dd>
            <dt className="text-[var(--color-muted)]">действие</dt>
            <dd>
              {actionLabel(v.action)}
              {v.confidence !== undefined && ` · уверенность ${pct(v.confidence, 0)}`}
            </dd>
            {extraAnswers(v).map((a) => (
              <Fragment key={a.name}>
                <dt className="text-[var(--color-muted)]">{a.name}</dt>
                <dd>{a.text}</dd>
              </Fragment>
            ))}
            {v.answer_plan && (
              <>
                <dt className="text-[var(--color-muted)]">цена в момент ответа</dt>
                <dd>
                  {v.answer_plan.reason
                    ? v.answer_plan.reason
                    : `${v.answer_plan.size} share за ${v.answer_plan.quote}${v.answer_plan.fee ? ` + ${v.answer_plan.fee}` : ""}`}
                </dd>
              </>
            )}
            {v.answer_pnl !== undefined && (
              <>
                <dt className="text-[var(--color-muted)]">итог, если бы послушали</dt>
                <dd>{money(v.answer_pnl)}</dd>
              </>
            )}
            {v.model && (
              <>
                <dt className="text-[var(--color-muted)]">модель</dt>
                <dd>{v.model}</dd>
              </>
            )}
          </dl>
          {inconsistent(v) && (
            <p className="mt-1 text-[var(--color-warn)]">действие противоречит собственной P(Up)</p>
          )}
          {v.error && <p className="mt-1 break-words text-[var(--color-bad)]">{v.error}</p>}
        </>
      )}
    </div>
  );
}

function History({
  asks,
  shown,
  onPick,
}: {
  asks: AIAskView[];
  shown: string | null;
  onPick: (id: string) => void;
}) {
  if (asks.length === 0) return null;
  return (
    <div className="mt-3 overflow-x-auto">
      <table className="w-full text-xs">
        <thead className="text-left text-[var(--color-muted)]">
          <tr>
            <th className="pb-1 font-normal">время</th>
            <th className="pb-1 font-normal">TypeSafe</th>
            <th className="pb-1 font-normal">OpenAI</th>
            <th className="pb-1 font-normal">исход</th>
          </tr>
        </thead>
        <tbody>
          {asks.map((a) => {
            const by = new Map(a.verdicts.map((x) => [x.provider, x]));
            const s = a.snapshot;
            const outcome =
              s?.resolved === undefined || s.resolved === null
                ? a.terminal
                  ? "ждём исхода"
                  : "…"
                : !s.resolved
                  ? "не подтверждён"
                  : s.winner_asset === s.up_asset_id
                    ? "Up"
                    : "Down";
            return (
              <tr
                key={a.id}
                className={`cursor-pointer border-t border-[var(--color-border)] hover:bg-[var(--color-panel)] ${
                  a.id === shown ? "bg-[var(--color-panel)]" : ""
                }`}
                onClick={() => onPick(a.id)}
              >
                <td className="py-1">{formatTime(a.requested_at)}</td>
                <td className="py-1">{cell(by.get("typesafe"), a)}</td>
                <td className="py-1">{cell(by.get("openai"), a)}</td>
                <td className="py-1">{outcome}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function cell(v: AIVerdict | undefined, a: AIAskView) {
  if (!v) return <span className="text-[var(--color-muted)]">{a.terminal ? a.view_status : "…"}</span>;
  return <span className={toneClass[verdictTone(v)]}>{verdictWord(v)}</span>;
}
